import { useEffect } from 'react';
import { AppShell } from './shell/AppShell';
import { OverviewPage } from './pages/OverviewPage';
import { LoginPage } from './pages/LoginPage';
import { Loader } from './components/States';
import { FilterProvider } from './lib/filter-context';
import { useAuth } from './lib/queries';
import type { AuthState } from '../shared/contracts';

export type AppView = 'loading' | 'redirect' | 'login' | 'app';

/**
 * Top-level view decision. A cold boot with Entra configured and no session auto-starts
 * Microsoft login (`redirect`); the login page shows only after an explicit logout
 * (`?loggedout`) or under dev-bypass. Production stays fail-closed regardless — the API
 * still requires a real session, so this is purely a client convenience.
 */
export function appView(auth: AuthState | undefined, isLoading: boolean, loggedOut: boolean): AppView {
  if (isLoading || !auth) return 'loading';
  if (auth.authenticated) return 'app';
  if (!auth.devBypass && !loggedOut) return 'redirect';
  return 'login';
}

function loggedOutFromUrl(): boolean {
  return new URLSearchParams(window.location.search).has('loggedout');
}

export default function App() {
  const { data: auth, isLoading } = useAuth();
  const view = appView(auth, isLoading, loggedOutFromUrl());

  useEffect(() => {
    if (view === 'redirect') window.location.assign('/auth/login');
  }, [view]);

  if (view === 'loading') return <Loader label="Indlæser …" />;
  if (view === 'redirect') return <Loader label="Sender dig til Microsoft-login …" />;
  if (view === 'login') return <LoginPage />;
  return (
    <FilterProvider>
      <AppShell>
        <OverviewPage />
      </AppShell>
    </FilterProvider>
  );
}
